<template>
    <ai-popup
        :title="`${state.roleCode} ${state.roleName}`"
        width="60%"
        height="75%"
        v-model:visible="state.roleAreasPopupVisible"
        @close-popup="onHidden"
    >
        <local-grid ref="areasGridRef" class="h-full" :data-source="dataSource" :meta="meta" :editing="editing">
            <template #toolbarItems>
                <dx-button class="ai-button-base" @click="refreshData" :disabled="changeRefreshDisable">
                    <ai-refresh-base />
                </dx-button>
            </template>
        </local-grid>
    </ai-popup>
</template>
<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { DxButton } from 'devextreme-vue';
import LocalGrid from '@/components/local-grid.vue';
import AiPopup from '@/components/ai-popup/ai-popup.vue';
import userLocalStore from '@/views/administration/users/store';
import rolesStore from '@/store/administration/roles';
import { dxUiConfirm, dxUiNotify } from '@/utils/dx-helper';

const { state, MODE, CARD_TABS, loadById } = userLocalStore;
const { rolesState, getRoleList } = rolesStore;

const areasGridRef = ref();
const gridInstance = ref(null);
const disabled = ref(true);

onMounted(() => {
    state.areaGridInstance = areasGridRef.value.gridInstance;
    gridInstance.value = areasGridRef.value.gridInstance;

    gridInstance.value.on('optionChanged', onOptionChanged);
    gridInstance.value.on('selectionChanged', onSelectionChanged);
    gridInstance.value.on('saving', onSaving);

    state.isClosingConfirmed = true;
});

const changeRefreshDisable = computed(() => {
    return state.mode === MODE.CREATE ? state.requestBodyHasNotChanged : false;
});

const onSelectionChanged = event => {
    disabled.value = !event.selectedRowKeys.length;
};

const dataSource = computed(() => {
    return [...state.areas, ...state.mutationAreas];
});

const onSaving = async event => {
    event.cancel = true;
    const result = [];

    state.areas.forEach(area => {
        for (const obj of event.changes) {
            if (area.displayId === obj.key) {
                area.permission = obj.type === 'update' ? obj.data.permission : area.permission;
            }
        }
        if (!area.isPreparedToRemove) result.push(area);
    });

    await getRoleList(state.appId);
    state.isClosingConfirmed = true;
    state.requestBodyHasNotChanged = true;
    state.roleAreasPopupVisible = false;
};

const onOptionChanged = event => {
    const { fullName, value } = event;
    if (fullName !== 'editing.changes') return;

    for (const obj of value) {
        if (obj.type === 'remove') {
            const removedRowId = obj.key;
            [...state.areas, ...state.mutationAreas].forEach(area => {
                if (area.displayId === removedRowId) {
                    area.isPreparedToRemove = true;
                }
            });
        }
    }
};

const onHidden = () => {
    state.roleAreasPopupVisible = false;
    state.isOpenInsertAreas = false;
    state.currentTabIndex = CARD_TABS.ROLES;
    if (state.isOpenedByUsersRole) {
        state.userCardPopupVisible = true;
        state.isOpenedByUsersRole = false;
    } else {
        state.usersRolePopupVisible = true;
    }
};

const refreshData = async () => {
    const result = await dxUiConfirm(
        'Вы действительно хотите сбросить изменения и обновить данные?',
        'Подтвердите действие',
    );
    if (!result) return;

    state.stopWatching();
    state.isClosingConfirmed = true;
    state.requestBodyHasNotChanged = true;

    state.areas = [];

    if (state.mode !== MODE.CREATE) {
        const { roleId, appId } = state;
        await loadById(roleId, appId);
    }

    state.stopWatching = watch(
        () => [state.areas],
        () => {
            state.isClosingConfirmed = false;
            state.requestBodyHasNotChanged = false;
        },
        { deep: true },
    );

    dxUiNotify('Данные обновлены', 'success');
};

const permissionType = [
    { permission: 'R', name: 'Чтение' },
    { permission: 'W', name: 'Запись' },
];

const editing = {
    mode: 'batch',
    allowUpdating: true,
};

const meta = {
    title: 'Список областей использования',
    keyExpr: 'displayId',
    scrollingMode: 'standart',
    selectionMode: 'none',
    columns: [
        {
            dataField: 'group_id',
            caption: 'Группа',
            groupIndex: 0,
            lookup: {
                dataSource: rolesState.groupList,
                valueExpr: 'id',
                displayExpr: 'name',
            },
        },
        {
            dataField: 'id',
            caption: 'id',
            visible: false,
        },
        {
            dataField: 'name',
            caption: 'Область использования',
            allowEditing: false,
        },
        {
            dataField: 'permission',
            caption: 'Тип доступа',
            allowEditing: false,
            lookup: {
                dataSource: permissionType,
                valueExpr: 'permission',
                displayExpr: 'name',
            },
        },
        {
            dataField: 'description',
            caption: 'Описание',
            allowEditing: false,
        },
    ],
};
</script>
